<script>
import SetDelivery from '@/components/organism/SetDelivery'
import ModalGroupOrder from '@/components/organism/modal/ModalGroupOrder'
import DisplayRate from '@/components/organism/restaurant/DisplayRate'
import Favourite from '@/components/molecules/Favourite'
import Modal from '@/components/atoms/Modal'
import InputMap from '@/components/atoms/inputlib/components/InputMap'
import getAddress from '@/modules/mixins/getAddress'
import { mapGetters } from 'vuex'

export default {
    name: 'RestaurantDetails',
    components: {
        Favourite,
        DisplayRate,
        ModalGroupOrder,
        SetDelivery,
        InputMap,
        Modal,
    },
    mixins: [getAddress],
    props: {
        restaurant: {
            type: Object,
            default: () => {},
        },
        allowance: {
            type: Object,
            default: () => {},
        },
    },

    data() {
        return {
            position: { longitude: 0, latitude: 0 },
        }
    },
    computed: {
        ...mapGetters(['smDevice', 'lgDevice']),
        calculation() {
            return this.$cart.calculation
        },
        name() {
            return this.restaurant?.name
        },
        tags() {
            return this.restaurant?.tags
        },
        description() {
            return this.restaurant?.description
        },
        isLoggedIn() {
            return this.$user.isLoggedIn
        },
        openingDays() {
            return this.rotateOpeningDays(
                !this.$moment().day() ? 6 : this.$moment().day() - 1,
                Object.values(
                    this.restaurant.opening_days.reduce(
                        (openingDays, { day, time_intervals, date }) => {
                            if (!date) {
                                openingDays[day].day = day
                                openingDays[day].timeIntervals = time_intervals
                                openingDays[day].open = openingDays[day].festiveIntervals
                                    ? !!openingDays[day].festiveIntervals.length
                                    : !!time_intervals.length
                            } else {
                                if (
                                    this.$moment(date).isBetween(
                                        this.$moment(),
                                        this.$moment().add(7, 'days'),
                                        'day',
                                        '[)'
                                    )
                                ) {
                                    openingDays[day].day = day
                                    openingDays[day].festiveIntervals = time_intervals
                                    openingDays[day].open = !!time_intervals.length
                                }
                            }
                            return openingDays
                        },
                        Object.fromEntries(
                            Array.from({ length: 7 }, (_, i) => [
                                i + 1,
                                { day: i + 1, timeIntervals: [], open: false },
                            ])
                        )
                    )
                )
            )
        },
    },
    methods: {
        rotateOpeningDays(rotateBy, arr) {
            const n = arr.length
            rotateBy %= n
            return arr.slice(rotateBy).concat(arr.slice(0, rotateBy))
        },
        getDay(day) {
            return this.$moment().isoWeekday(day).format('dddd')
        },
        getDefaultHours(timeIntervals) {
            if (!timeIntervals?.length) return `<br/>${this.$t('base.closed')}`
            return timeIntervals.reduce((text, interval) => {
                return `${text}<br/>${interval.open}-${interval.close}`
            }, '')
        },
        openInfoPanel() {
            if (!this.restaurant.latitude) {
                this.getAddress({ address: this.restaurant.location }).then((res) => {
                    this.position.latitude = res?.geometry?.location?.lat
                    this.position.longitude = res?.geometry?.location?.lng
                    this.$nextTick(() => {
                        this.$refs.description.openModal()
                    })
                })
            } else {
                this.position.latitude = this.restaurant.latitude
                this.position.longitude = this.restaurant.longitude
                this.$nextTick(() => {
                    this.$refs.description.openModal()
                })
            }
        },
        closeInfoPanel() {
            this.$refs.description.closeModal()
        },
        copyLink() {
            navigator.permissions.query({ name: 'clipboard-write' }).then((result) => {
                if (result.state == 'granted' || result.state == 'prompt') {
                    navigator.clipboard.writeText(window.location.href).then(
                        () => {
                            this.$store.dispatch('toastSuccess', {
                                title: this.$t('restaurant.copy_link'),
                                text: this.$t('restaurant.copy_link_success'),
                            })
                        },
                        () => {
                            this.$store.dispatch('toastWarning', {
                                title: this.$t('restaurant.copy_link'),
                                text: this.$t('restaurant.copy_link_fail'),
                            })
                        }
                    )
                }
            })
        },
    },
}
</script>

<template lang="pug">
.restaurant-details.d-flex.flex-column.overflow-hidden
    .d-flex.align-items-center.justify-content-between.mb-3
        h1.mb-0 {{name}}
        favourite.ml-3(v-if="isLoggedIn" :restaurant="restaurant")
    .d-flex.align-items-center.mb-5
        .d-flex.flex-md-wrap.align-items-baseline(:class="{'w-0': smDevice}")
            display-rate.separator.flex-shrink-0.mr-4(:model="restaurant" reverse)
            span.separator.text-m.flex-shrink-0(v-for="(tag, index) in tags" v-if="index < 10" :key="`restaurantDetailsTag-${tag.id}`") {{tag.name_token}}
            .flex-shrink-0(v-if="tags && tags.length > 10") ...
        span.more-button.ml-auto(v-if="smDevice" role="button" @click="openInfoPanel") {{$t('restaurant.info_map')}}

    .description
        p.body-m.mb-0(v-html="restaurant.description" :class="smDevice ? 'text-ellipsis-2' : 'text-ellipsis-3'")
        span.more-button(v-if="lgDevice" role="button" @click="openInfoPanel") {{$t('restaurant.info_map')}}
    modal(ref="description" scroll)
        template(v-slot:header)
            .modal-header-text {{$t('restaurant.info')}}
        template(v-slot:body)
            input-map(:value="position" zoom ref="map")
            h1.text-center.mt-6 {{restaurant.name}}
            p.body-m.text-left.text-lg-center.font-weight-semibold(v-if="restaurant.description" v-html="restaurant.description")
            .d-flex.flex-wrap.mb-5.justify-content-center
                span.separator.text-m(v-for="(tag, index) in tags" :key="`restaurantDetailsTag-${tag.id}`") {{tag.name_token}}
            .row.mx-n3.mb-5(v-if="openingDays.length")
                .col-12.px-3
                    h2.text-center {{$t('restaurant.opening_hours')}}
                template(v-for="(openingDay, index) in openingDays")
                    .col-6.px-3.text-sm.text-right(:key="`openingHoursDay-${index}`") {{ getDay(openingDay.day) }}:
                    .col-6.px-3.d-flex.flex-column.text-sm(:key="`openingHoursTime-${index}`")
                        span(v-if="!openingDay.open") {{$t('base.closed')}}                            
                            icon.ml-2.icon-baseline.text-primary(v-if="openingDay.festiveIntervals" v-tooltip.hover.top.html="$t('base.default_hours', {time: getDefaultHours(openingDay.timeIntervals)})" icon="fa-solid fa-info-circle")
                        template(v-else)
                            template(v-if="openingDay.festiveIntervals")
                                span(v-for="(festiveDay, index) in openingDay.festiveIntervals") {{festiveDay.open}} - {{festiveDay.close}}                                                          
                                    icon.ml-2.icon-baseline.text-primary(v-if="!index" v-tooltip.hover.top.html="$t('base.default_hours', {time: getDefaultHours(openingDay.timeIntervals)})" icon="fa-solid fa-info-circle")
                            template(v-else)
                                span(v-for="day in openingDay.timeIntervals") {{day.open}} - {{day.close}}                        
            .flex-center
                span.text-m.mb-2.my-2.text-center
                    icon.text-99.mr-4(icon="fa-solid fa-map-marker-alt")
                    | {{restaurant.location}}
        template(v-slot:footer)
            button.btn.btn-primary.order-1.order-lg-0.mr-lg-4(@click="closeInfoPanel") {{$t('base.close')}}
            button.btn.btn-secondary.order-0.order-lg-1.mr-4.mr-lg-0(v-if="!$user.onlyCloudCanteen" @click="copyLink")
                icon(icon="fa-solid fa-clone")
                span {{$t('restaurant.copy_link')}}
    //set-delivery
    modal-group-order.mt-auto.ml-auto
</template>
